import {
  Button,
  Dropdown,
  Row,
  Col,
  Empty,
  Modal,
  Table,
  Tag,
  Switch,
} from "antd";
import { EditFilled, MoreOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import deleteIcon from "../../assets/images/webToLead/delete.svg";

import { useAlert } from "../../context/AlertProvider";
import {
  createLeadCustomFieldDefinition,
  deleteLeadCustomFieldDefinition,
  updateLeadCustomFieldDefinition,
} from "../../actionCreator/leadCustomFieldDefinitionsActionCreator";
import { useLeadCustomFieldDefinitions } from "../../common/hooks/useLeadCustomFieldDefinitions";
import { getLeadCustomFieldIsActive } from "../../utils/leadCustomFields";
import LeadCustomFieldFormDrawer from "./LeadCustomFieldFormDrawer";

const LeadCustomFields = ({ canView, canCreate, canEdit, canDelete }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { fields, loading, refreshFields } = useLeadCustomFieldDefinitions();
  const [editingField, setEditingField] = useState();
  const [deleteField, setDeleteField] = useState();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getFieldKey = (field) => field?.id ?? field?.fieldName;

  const handleError = (error, fallbackMessage) => {
    if (error?.response?.status === 403) {
      showAlert("warning", "Permission Denied", fallbackMessage);
      return;
    }

    showAlert(
      "error",
      error?.response?.data?.title || "Something went wrong. Please try again.",
    );
  };

  const handleSave = async (payload) => {
    setSubmitting(true);

    const res = editingField?.id
      ? await dispatch(updateLeadCustomFieldDefinition(payload))
      : await dispatch(createLeadCustomFieldDefinition(payload));

    setSubmitting(false);

    if (res?.status === 200 || res?.status === 201) {
      await refreshFields(false);
      setOpenDrawer(false);
      setEditingField(undefined);
      showAlert(
        "success",
        editingField?.id
          ? "Lead custom field updated successfully."
          : "Lead custom field created successfully.",
      );
      return;
    }

    handleError(
      res,
      editingField?.id
        ? "You do not have permission to edit lead custom fields."
        : "You do not have permission to create lead custom fields.",
    );
  };

  const columns = useMemo(
    () => [
      {
        title: <span className="tw-text-sm ">Label Name</span>,
        dataIndex: "fieldName",
        key: "fieldName",
        render: (value, row) => (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm">{value}</span>
            {!row?.isOptional && (
              <Tag className="tw-rounded-full tw-border-0 tw-bg-[#FDECEC] tw-text-[#B42318]">
                Mandatory
              </Tag>
            )}
          </div>
        ),
      },
      {
        title: <span className="tw-text-sm ">Field Type</span>,
        dataIndex: "fieldType",
        key: "fieldType",
        render: (value) => <span className="tw-text-sm">{value}</span>,
      },
      {
        title: <span className="tw-text-sm ">Default Value</span>,
        dataIndex: "defaultValue",
        key: "defaultValue",
        render: (value) => <span className="tw-text-sm">{value || "-"}</span>,
      },
      {
        title: <span className="tw-text-sm ">Status</span>,
        dataIndex: "isActive",
        key: "isActive",
        render: (value, row) => (
          <Switch
            checked={getLeadCustomFieldIsActive(row)}
            disabled={!canEdit || submitting}
            onChange={async (checked) => {
              if (!canEdit) return;

              setSubmitting(true);
              const res = await dispatch(
                updateLeadCustomFieldDefinition({
                  ...row,
                  isActive: checked,
                }),
              );
              setSubmitting(false);

              if (res?.status === 200) {
                await refreshFields(false);
                showAlert(
                  "success",
                  `Lead custom field ${checked ? "activated" : "deactivated"} successfully.`,
                );
                return;
              }

              handleError(
                res,
                "You do not have permission to edit lead custom fields.",
              );
            }}
          />
        ),
      },
      {
        title: <span className="tw-text-sm ">Actions</span>,
        key: "actions",
        width: 120,
        render: (_, row) => {
          const items = [
            {
              key: "1",
              label: (
                <Row
                  gutter={12}
                  className="heading14px tw-px-1"
                  style={{ color: "#29292B", opacity: canEdit ? 1 : 0.5 }}
                  onClick={() => {
                    if (!canEdit) {
                      showAlert(
                        "warning",
                        "Permission Denied",
                        "You do not have permission to edit lead custom fields.",
                      );
                      return;
                    }
                    setEditingField(row);
                    setOpenDrawer(true);
                  }}
                >
                  <Col span={6} className="!tw-px-0">
                    <EditFilled />
                  </Col>
                  <Col span={18}>Edit</Col>
                </Row>
              ),
            },
            {
              key: "2",
              label: (
                <Row
                  gutter={12}
                  className="heading14px tw-px-1"
                  style={{ color: "#29292B", opacity: canDelete ? 1 : 0.5 }}
                  onClick={() => {
                    if (!canDelete) {
                      showAlert(
                        "warning",
                        "Permission Denied",
                        "You do not have permission to delete lead custom fields.",
                      );
                      return;
                    }
                    setDeleteField(row);
                  }}
                >
                  <Col span={6} className="!tw-px-0">
                    <img
                      src={deleteIcon}
                      alt=""
                      width={20}
                      height={15}
                      className="cursor-pointer"
                    />
                  </Col>
                  <Col span={18} className="heading14px">
                    Delete
                  </Col>
                </Row>
              ),
            },
          ];

          return (
            <Dropdown menu={{ items }} trigger={["click"]}>
              <Button type="text" className="tw-more-btn" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [canDelete, canEdit, dispatch, refreshFields, showAlert, submitting],
  );

  if (!canView) {
    return (
      <section className="p-20 h-100">
        <div className="tw-rounded-2xl tw-bg-white tw-p-8">
          <Empty description="You do not have permission to view lead custom fields." />
        </div>
      </section>
    );
  }

  return (
    <section className="p-20 h-100 tw-font-inter">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
        <div className="tw-flex tw-items-center tw-gap-4">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-p-2 tw-bg-slate-100 hover:tw-bg-slate-200/60 tw-text-slate-600 tw-transition-colors"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
          </div>
          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-0.5">
            <h1 className="tw-text-xl tw-font-bold tw-mb-0 tw-text-slate-800 tw-font-inter">
              Lead Custom Fields
            </h1>
            <p className="tw-text-xs tw-mb-0 tw-text-slate-500 tw-font-medium">
              Create and manage custom fields that should appear on lead create and edit forms.
            </p>
          </div>
        </div>
        <Button
          type="primary"
          className="tw-btn-premium"
          onClick={() => {
            if (!canCreate) {
              showAlert(
                "warning",
                "Permission Denied",
                "You do not have permission to create lead custom fields.",
              );
              return;
            }

            setEditingField(undefined);
            setOpenDrawer(true);
          }}
        >
          <Plus size={16} /> Add Custom Field
        </Button>
      </div>

      <div className="tw-rounded-2xl tw-bg-white">
        <Table
          rowKey={getFieldKey}
          columns={columns}
          pagination={false}
          dataSource={fields}
          loading={loading}
          size="middle"
          className="tw-custom-table tw-table-max-content"
          locale={{
            emptyText: (
              <Empty description="No lead custom fields created yet." />
            ),
          }}
        />
      </div>

      <LeadCustomFieldFormDrawer
        open={openDrawer}
        initialValues={editingField}
        canManage={editingField ? canEdit : canCreate}
        existingFieldNames={fields
          .map((field) => field?.fieldName)
          .filter((fieldName) => fieldName !== editingField?.fieldName)}
        submitting={submitting}
        onClose={() => {
          setOpenDrawer(false);
          setEditingField(undefined);
        }}
        onSubmit={handleSave}
      />

      <Modal
        title={null}
        className="delete-popup tw-overflow-hidden"
        open={!!deleteField}
        centered
        footer={null}
        onCancel={() => setDeleteField(undefined)}
      >
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50 tw-font-inter">
          Delete Lead Custom Field
        </div>
        <div style={{ padding: "24px" }} className="tw-font-inter tw-text-sm tw-text-slate-600">
          Are you sure you want to delete "{deleteField?.fieldName}"? Existing
          lead records will no longer show this managed field.
        </div>
        <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
          <Button
            className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
            onClick={() => setDeleteField(undefined)}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={deleting}
            className="!tw-h-[38px] !tw-rounded-lg !tw-bg-red-600 !tw-border-red-600 !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:!tw-bg-red-700 hover:!tw-border-red-700 tw-transition-all tw-duration-200"
            onClick={async () => {
              if (!deleteField?.id) {
                showAlert("error", "Unable to delete this lead custom field.");
                return;
              }

              setDeleting(true);
              const res = await dispatch(
                deleteLeadCustomFieldDefinition(deleteField.id),
              );
              setDeleting(false);

              if (res?.status === 204 || res?.status === 200) {
                await refreshFields(false);
                setDeleteField(undefined);
                showAlert("success", "Lead custom field deleted successfully.");
                return;
              }

              handleError(
                res,
                "You do not have permission to delete lead custom fields.",
              );
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </section>
  );
};

export default LeadCustomFields;
